import { useEffect, useState, useCallback } from 'react'

// Breakpoints matching portal-responsive.css
export const BREAKPOINTS = {
  mobile: 480,
  tablet: 768,
  desktop: 1024,
  largeDesktop: 1440
} as const

export type DeviceType = 'mobile' | 'tablet' | 'desktop' | 'largeDesktop'

interface MobileState {
  isMobile: boolean
  isTablet: boolean
  isDesktop: boolean
  isLargeDesktop: boolean
  deviceType: DeviceType
  width: number
  height: number
  isPortrait: boolean
  isLandscape: boolean
  isTouchDevice: boolean
  safeAreaInsets: {
    top: number
    bottom: number
    left: number
    right: number
  }
}

/**
 * Enhanced mobile detection hook with comprehensive device info
 * Provides reactive viewport info, device type, orientation, and safe area insets
 */
export function useMobile(): MobileState {
  const [state, setState] = useState<MobileState>(() => getInitialState())

  useEffect(() => {
    const updateState = () => {
      setState(getInitialState())
    }

    // Listen to resize events
    window.addEventListener('resize', updateState)

    // Listen to orientation change
    window.addEventListener('orientationchange', updateState)

    // Initial check
    updateState()

    return () => {
      window.removeEventListener('resize', updateState)
      window.removeEventListener('orientationchange', updateState)
    }
  }, [])

  return state
}

function getInitialState(): MobileState {
  if (typeof window === 'undefined') {
    return {
      isMobile: false,
      isTablet: false,
      isDesktop: true,
      isLargeDesktop: false,
      deviceType: 'desktop',
      width: 1024,
      height: 768,
      isPortrait: false,
      isLandscape: true,
      isTouchDevice: false,
      safeAreaInsets: { top: 0, bottom: 0, left: 0, right: 0 }
    }
  }

  const width = window.innerWidth
  const height = window.innerHeight
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0

  const isMobile = width < BREAKPOINTS.tablet
  const isTablet = width >= BREAKPOINTS.tablet && width < BREAKPOINTS.desktop
  const isDesktop = width >= BREAKPOINTS.desktop && width < BREAKPOINTS.largeDesktop
  const isLargeDesktop = width >= BREAKPOINTS.largeDesktop

  let deviceType: DeviceType = 'desktop'
  if (isMobile) deviceType = 'mobile'
  else if (isTablet) deviceType = 'tablet'
  else if (isLargeDesktop) deviceType = 'largeDesktop'

  // Get safe area insets (for iOS notch, etc.)
  const safeAreaInsets = getSafeAreaInsets()

  return {
    isMobile,
    isTablet,
    isDesktop,
    isLargeDesktop,
    deviceType,
    width,
    height,
    isPortrait: height > width,
    isLandscape: width > height,
    isTouchDevice,
    safeAreaInsets
  }
}

function getSafeAreaInsets() {
  if (typeof window === 'undefined' || typeof getComputedStyle === 'undefined') {
    return { top: 0, bottom: 0, left: 0, right: 0 }
  }

  const style = getComputedStyle(document.documentElement)

  return {
    top: parseInt(style.getPropertyValue('--sat') || '0', 10) ||
         parseInt(style.getPropertyValue('env(safe-area-inset-top)') || '0', 10),
    bottom: parseInt(style.getPropertyValue('--sab') || '0', 10) ||
            parseInt(style.getPropertyValue('env(safe-area-inset-bottom)') || '0', 10),
    left: parseInt(style.getPropertyValue('--sal') || '0', 10) ||
          parseInt(style.getPropertyValue('env(safe-area-inset-left)') || '0', 10),
    right: parseInt(style.getPropertyValue('--sar') || '0', 10) ||
           parseInt(style.getPropertyValue('env(safe-area-inset-right)') || '0', 10)
  }
}

/**
 * Hook for handling viewport height on iOS (100vh bug fix)
 * Sets --vh CSS variable that accounts for browser chrome
 */
export function useViewportHeight() {
  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty('--vh', `${vh}px`)
    }

    setVH()
    window.addEventListener('resize', setVH)
    window.addEventListener('orientationchange', setVH)

    return () => {
      window.removeEventListener('resize', setVH)
      window.removeEventListener('orientationchange', setVH)
    }
  }, [])
}

/**
 * Hook for safe area insets - updates CSS variables
 */
export function useSafeArea() {
  useEffect(() => {
    // Set CSS variables for safe area insets
    const root = document.documentElement
    root.style.setProperty('--sat', 'env(safe-area-inset-top)')
    root.style.setProperty('--sab', 'env(safe-area-inset-bottom)')
    root.style.setProperty('--sal', 'env(safe-area-inset-left)')
    root.style.setProperty('--sar', 'env(safe-area-inset-right)')
  }, [])
}

/**
 * Simple haptic feedback utility
 */
export function triggerHaptic(type: 'light' | 'medium' | 'heavy' = 'light') {
  if ('vibrate' in navigator) {
    const duration = type === 'light' ? 10 : type === 'medium' ? 25 : 50
    navigator.vibrate(duration)
  }
}

/**
 * Check if device prefers reduced motion
 */
export function usePrefersReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    setPrefersReducedMotion(mediaQuery.matches)

    const handler = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches)
    }

    mediaQuery.addEventListener('change', handler)
    return () => mediaQuery.removeEventListener('change', handler)
  }, [])

  return prefersReducedMotion
}
